/** @format */

import React, { useState, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { NavLink } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import {
  ChevronDown,
  Car,
  ShoppingBag,
  Calendar,
  Package,
  Lock,
  Newspaper,
  Layout,
  ShoppingCart,
  Binary,
  Home,
  Handshake,
  Mail,
  Wrench,
  User,
  FileText,
  Settings,
} from "lucide-react";

// Définition des menus
const getMenuItems = (isAuthenticated) => ({
  auto: [
    {
      title: "Calendrier Mécanique",
      path: "/pole-auto/calendrier",
      icon: Calendar,
      requireAuth: true,
    },
    {
      title: "Gestion Matériel",
      path: "/pole-auto/materiel",
      icon: Package,
      requireAuth: true,
    },
    {
      title: "Application garage",
      path: "/pole-auto/application",
      icon: Wrench,
      requireAuth: true,
    },
    {
      title: "Actualités Auto",
      path: "/pole-auto/actualites",
      icon: Newspaper,
      requireAuth: false,
    },
    {
      title: "Émargement CQP",
      path: "/pole-auto/emargement",
      icon: FileText,
      requireAuth: true,
    },
  ],
  vente: [
    {
      title: "WordPress",
      path: "/pole-vente/wordpress",
      icon: Layout,
      requireAuth: false,
    },
    {
      title: "PrestaShop",
      path: "/pole-vente/prestashop",
      icon: ShoppingCart,
      requireAuth: false,
    },
    {
      title: "Actualités Vente",
      path: "/pole-vente/actualites",
      icon: Binary,
      requireAuth: false,
    },
  ],
});

// Composant NavItem
const NavItem = ({ to, icon: Icon, children, requireAuth }) => {
  const { user } = useAuth();

  if (requireAuth && !user) {
    return (
      <NavLink
        to='/login'
        state={{ from: to }}
        className='flex items-center space-x-2 px-4 py-2 text-gray-400 hover:bg-slate-700'>
        <Lock className='w-4 h-4' />
        <span>
          {children} <span className='text-xs'>(Connexion requise)</span>
        </span>
      </NavLink>
    );
  }

  return (
    <NavLink
      to={to}
      className={({ isActive }) =>
        `flex items-center space-x-1 text-white hover:text-blue-400 transition-colors ${
          isActive ? "text-blue-400 font-semibold" : ""
        }`
      }>
      <Icon className='w-4 h-4' />
      <span>{children}</span>
    </NavLink>
  );
};

// Animations
const itemVariants = {
  hidden: { x: -10, opacity: 0 },
  visible: {
    x: 0,
    opacity: 1,
    transition: { duration: 0.1 },
  },
};

const dropdownVariants = {
  hidden: {
    opacity: 0,
    y: -10,
    transition: { duration: 0.1 },
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.15,
      staggerChildren: 0.03,
    },
  },
};

// Composant DropdownMenu
const DropdownMenu = React.memo(({ items, isOpen, variants }) => {
  const { user } = useAuth();

  const renderDropdownItem = useCallback(
    ({ path, icon: Icon, title, requireAuth }) => {
      if (requireAuth && !user) {
        return (
          <motion.div key={path} variants={itemVariants}>
            <NavLink
              to='/login'
              state={{ from: path }}
              className='flex items-center space-x-2 px-4 py-2 text-gray-400 hover:bg-slate-700'>
              <Lock className='w-4 h-4' />
              <span>
                {title} <span className='text-xs'>(Connexion requise)</span>
              </span>
            </NavLink>
          </motion.div>
        );
      }

      return (
        <motion.div key={path} variants={itemVariants}>
          <NavLink
            to={path}
            className={({ isActive }) =>
              `flex items-center space-x-2 px-4 py-2 text-white hover:bg-slate-700 ${
                isActive ? "bg-slate-700" : ""
              }`
            }>
            <Icon className='w-4 h-4' />
            <span>{title}</span>
          </NavLink>
        </motion.div>
      );
    },
    [user]
  );

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          variants={variants}
          initial='hidden'
          animate='visible'
          exit='hidden'
          className='absolute top-full left-0 w-64 bg-slate-800 rounded-lg shadow-lg py-2 mt-2'>
          {items.map(renderDropdownItem)}
        </motion.div>
      )}
    </AnimatePresence>
  );
});

DropdownMenu.displayName = "DropdownMenu";

// Composant principal
const MainNavigation = () => {
  const [activeDropdown, setActiveDropdown] = useState(null);
  const { user, userRole } = useAuth();
  const menuItems = getMenuItems(!!user);

  const handleMouseEnter = useCallback((menu) => {
    setActiveDropdown(menu);
  }, []);

  const handleMouseLeave = useCallback(() => {
    setActiveDropdown(null);
  }, []);

  const isAdmin = ["super_admin", "admin", "formateur"].includes(userRole);

  console.log("🔍 Navigation - userRole:", userRole);
  console.log("🔍 Navigation - isAdmin:", isAdmin);
  console.log("🔍 Navigation - user:", user?.email);

  return (
    <nav className='fixed top-20 left-0 right-0 z-40 bg-slate-800/50 backdrop-blur-md'>
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.2 }}
        className='container mx-auto px-4'>
        <ul className='flex items-center justify-center space-x-8 h-12'>
          {/* Accueil */}
          <motion.li
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.2 }}>
            <NavItem to='/' icon={Home}>
              Accueil
            </NavItem>
          </motion.li>

          {/* Pôle Auto */}
          <motion.li
            className='relative'
            onMouseEnter={() => handleMouseEnter("auto")}
            onMouseLeave={handleMouseLeave}
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.2 }}>
            <button className='flex items-center space-x-1 text-white hover:text-blue-400 transition-colors'>
              <Car className='w-4 h-4' />
              <span>FACTORY Mobilité</span>
              <motion.span
                animate={{ rotate: activeDropdown === "auto" ? 180 : 0 }}
                transition={{ duration: 0.15 }}>
                <ChevronDown className='w-4 h-4' />
              </motion.span>
            </button>

            <DropdownMenu
              items={menuItems.auto}
              isOpen={activeDropdown === "auto"}
              variants={dropdownVariants}
            />
          </motion.li>
          
          {/* Actualités CFA */}
          <motion.li
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.2 }}>
            <NavItem to='/actualites-cfa' icon={Newspaper}>
              Actualités CFA
            </NavItem>
          </motion.li>

          {/* Pôle Vente */}
          <motion.li
            className='relative'
            onMouseEnter={() => handleMouseEnter("vente")}
            onMouseLeave={handleMouseLeave}
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.2 }}>
            <button className='flex items-center space-x-1 text-white hover:text-blue-400 transition-colors'>
              <ShoppingBag className='w-4 h-4' />
              <span>FACTORY Business</span>
              <motion.span
                animate={{ rotate: activeDropdown === "vente" ? 180 : 0 }}
                transition={{ duration: 0.15 }}>
                <ChevronDown className='w-4 h-4' />
              </motion.span>
            </button>

            <DropdownMenu
              items={menuItems.vente}
              isOpen={activeDropdown === "vente"}
              variants={dropdownVariants}
            />
          </motion.li>

          {/* Partenaires */}
          <motion.li
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.2 }}>
            <NavItem to='/partenaires' icon={Handshake}>
              Partenaires
            </NavItem>
          </motion.li>

          {/* Contact */}
          <motion.li
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.2 }}>
            <NavItem to='/contact' icon={Mail}>
              Contact
            </NavItem>
          </motion.li>

          {/* Profil (visible uniquement si connecté) */}
          {user && (
            <motion.li
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.2 }}>
              <NavItem to='/profile' icon={User}>
                Profil
              </NavItem>
            </motion.li>
          )}

          {/* Admin (visible uniquement si admin/super_admin/formateur) */}
          {isAdmin && (
            <motion.li
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.2 }}>
              <NavItem to='/admin' icon={Settings}>
                Administration
              </NavItem>
            </motion.li>
          )}
        </ul>
      </motion.div>
    </nav>
  );
};

export default MainNavigation;